'use client';

import Link from 'next/link';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import StepCards, { StepCardItem } from '../_shared/sections/StepCards';
import { useTranslation } from '@/i18n/client';
import { HiArrowRight, HiCalendarDays, HiCreditCard, HiEnvelope, HiUserPlus } from 'react-icons/hi2';

type Props = { lang: Locale };

type StepKey = 'create' | 'tickets' | 'attendee' | 'entry';

const STEP_NUMS: Record<StepKey, string> = {
  create: '01',
  tickets: '02',
  attendee: '03',
  entry: '04',
};

const STEP_ICONS: Record<StepKey, StepCardItem['Icon']> = {
  create: HiCalendarDays,
  tickets: HiCreditCard,
  attendee: HiUserPlus,
  entry: HiEnvelope,
};

const KEYS: StepKey[] = ['create', 'tickets', 'attendee', 'entry'];

const EventManagementHowItWorks = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const label = t('EventManagement/HowItWorks/badge');
  const headlineA = t('EventManagement/HowItWorks/title1');
  const headlineB = t('EventManagement/HowItWorks/title2');
  const sub = t('EventManagement/HowItWorks/subtitle');

  const steps: StepCardItem[] = KEYS.map((k) => ({
    key: k,
    num: STEP_NUMS[k],
    title: t(`EventManagement/HowItWorks/${k}/title`),
    description: t(`EventManagement/HowItWorks/${k}/body`),
    Icon: STEP_ICONS[k],
    footer:
      k === 'create' ? (
        <Link
          href={`/${lang}/events`}
          className="mono inline-flex items-center gap-2"
          style={{
            fontSize: 13,
            color: '#22D3EE',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            textDecoration: 'none',
            borderBottom: '1px dashed rgba(34,211,238,0.4)',
            paddingBottom: 2,
          }}
        >
          {t('EventManagement/HowItWorks/create/cta_label')}
          <HiArrowRight aria-hidden="true" className="w-4 h-4" />
        </Link>
      ) : undefined,
  }));

  return (
    <section
      id="how-it-works"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {sub}
        </p>

        <StepCards steps={steps} accent="#22D3EE" minCardWidth={240} showArrow />
      </div>
    </section>
  );
};

export default EventManagementHowItWorks;
